/**
 * An (i) icon that reveals a heading's caveat or explanation on click.
 *
 * Replaces a pattern repeated across the dashboard and candidate scorecard:
 * a bold section heading immediately followed by a full sentence of
 * permanent, always-visible caption text -- most of it explaining an API
 * limitation or what the number does NOT mean. That is real information a
 * reader occasionally needs, not something every view should pay the
 * vertical space and attention cost for on every single load.
 *
 * Popover, not Tooltip: this app already has mobile breakpoints, and hover
 * has no equivalent on touch. One click-driven interaction that behaves
 * identically on desktop and mobile beats hover-on-one, something-else-on-
 * the-other.
 */

import { memo, type ReactNode } from 'react';
import { Popover } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import styles from './index.module.css';

export interface CaptionInfoProps {
  /** What the icon explains. Kept to one short, direct sentence. */
  text: string;
  /** Read by assistive tech; defaults to a generic label. */
  label?: string;
  /** Marks the icon with the page's existing accent, for a caption that
   * flags AI-assessed content changed rather than explaining a caveat --
   * e.g. a dimension score the interview actually moved. Every other call
   * site keeps the default muted styling. */
  accent?: boolean;
  /** Custom click trigger -- a role tag, a badge -- instead of the default
   * (i) icon button. The Popover/click mechanics are identical either way;
   * this exists so a second disclosure pattern is never needed just because
   * the trigger element isn't an icon. Omitted, this behaves exactly as
   * before. */
  children?: ReactNode;
}

export const CaptionInfo = memo(
  ({ text, label, accent, children }: CaptionInfoProps) => (
    <Popover
      content={<span className={styles.content}>{text}</span>}
      trigger="click"
    >
      {children ?? (
        <button
          type="button"
          className={
            accent ? `${styles.trigger} ${styles.accent}` : styles.trigger
          }
          aria-label={label ?? 'More information'}
        >
          <InfoCircleOutlined />
        </button>
      )}
    </Popover>
  ),
);

CaptionInfo.displayName = 'CaptionInfo';

export default CaptionInfo;
